import { h, Component } from 'preact';

import pickScene, {
    SCENE_MARKSMAN, SCENE_VET_DOG, SCENE_RUNNER, SCENE_MUSHING, SCENE_PAPER_PLANE,
} from './scenes/pickScene';
import { getPalette } from './scenes/palette';
import Marksman from './scenes/marksman';
import VetDog from './scenes/vetDog';
import Runner from './scenes/runner';
import Mushing from './scenes/mushing';
import PaperPlane from './scenes/paperPlane';

// Must match the gesture thresholds in app.js.
const refreshRange = 150;

const SCENE_COMPONENTS = {
    [SCENE_MARKSMAN]: Marksman,
    [SCENE_VET_DOG]: VetDog,
    [SCENE_RUNNER]: Runner,
    [SCENE_MUSHING]: Mushing,
    [SCENE_PAPER_PLANE]: PaperPlane,
};

const RELEASED_BEAT_MS = 600;
// Height of the rounded content-sheet edge drawn at the bottom of the band.
export const LIP_HEIGHT = 12;
// Height of the bottom-anchored scene composition (matches the 150-unit viewBox).
const SCENE_HEIGHT = 150;
// Must match LAYER_EASING in scenes/parallax.js so layers track the band edge.
const BAND_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

class Refresh extends Component {
    constructor(props) {
        super(props);
        this.state = { releasedAt: 0 };
        this.releaseTimer = null;
    }

    componentDidUpdate(prevProps) {
        // refreshing went true → false: play the release beat once.
        if (prevProps.refreshing && !this.props.refreshing) {
            this.setState({ releasedAt: Date.now() });
            clearTimeout(this.releaseTimer);
            this.releaseTimer = setTimeout(
                () => this.setState({ releasedAt: 0 }),
                RELEASED_BEAT_MS,
            );
        }
    }

    componentWillUnmount() {
        clearTimeout(this.releaseTimer);
    }

    render() {
        const { height = 0, refreshing = false, sceneOverride } = this.props;
        const { appState } = this.props.stores;
        const { currentCustomer = {}, darkmode } = appState;
        const { releasedAt } = this.state;

        const progress = Math.min(height / refreshRange, 1);
        const pastThreshold = height >= refreshRange;
        const released = releasedAt > 0;
        // Hold the scene fully visible while the release beat plays — the band
        // collapses (height → 0) the moment refreshing ends, and the burst
        // animations (0.7-0.8s) would otherwise fade out after ~100ms.
        const opacity = released ? 1 : Math.min(progress * 1.5, 1);
        // Negative while pulling, 0 at the settled refresh height, positive on overshoot.
        const pullOffset = height - refreshRange;

        const sceneId = sceneOverride || pickScene(currentCustomer);
        const SceneComponent = SCENE_COMPONENTS[sceneId] || PaperPlane;
        const palette = getPalette(sceneId, !!darkmode);

        return (
            <div
                class='position-relative overflow-hidden w-100'
                style={`
                    height: ${height}px;
                    transition: height 0.3s ${BAND_EASING};
                    background: ${palette.backdrop};
                `}
            >
                {/* Bottom-anchored composition: rides the sheet edge; layers counter-translate. */}
                <div
                    style={`
                        position: absolute; left: 0; right: 0;
                        bottom: ${LIP_HEIGHT}px; height: ${SCENE_HEIGHT}px;
                        overflow: visible;
                        opacity: ${opacity};
                        transition: opacity 0.1s ease;
                    `}
                >
                    <SceneComponent
                        progress={progress}
                        pullOffset={pullOffset}
                        pastThreshold={pastThreshold}
                        refreshing={refreshing}
                        released={released}
                        palette={palette}
                        dark={!!darkmode}
                    />
                </div>

                {height > 60 && (
                    <small
                        style={`
                            position: absolute; left: 0; right: 0; bottom: ${LIP_HEIGHT + 4}px;
                            text-align: center;
                            font-size: 13px;
                            font-weight: 600;
                            color: ${pastThreshold ? '#28a745' : palette.text};
                            opacity: ${pastThreshold ? 1 : 0.7};
                            text-shadow: 0 1px 2px ${palette.backdrop};
                            transition: color 0.2s ease, opacity 0.2s ease;
                            pointer-events: none;
                        `}
                    >
                        {refreshing
                            ? 'Oppdaterer...'
                            : (pastThreshold ? 'Slipp for å oppdatere' : 'Dra ned for å oppdatere')}
                    </small>
                )}

                {/* Content-sheet lip — same surface as the page (bg-light incl. its dark override),
                    so the content below reads as a card sliding over the scene. -1px overlap
                    avoids a hairline seam against the content. */}
                <div
                    class='bg-light'
                    style={`
                        position: absolute; left: 0; right: 0; bottom: -1px;
                        height: ${LIP_HEIGHT + 1}px;
                        border-radius: 10px 10px 0 0;
                        box-shadow: 0 -8px 20px ${darkmode ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.12)'};
                    `}
                />
            </div>
        );
    }
}

export default Refresh;
